<section class="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-800 dark:bg-neutral-800/40">
  <div class="mb-2.5 flex items-center justify-between">
    <h2 class="text-lg font-semibold text-neutral-900 dark:text-white">Linha do tempo</h2>
    @if (!loading) {
      <div class="text-xs text-neutral-500 dark:text-neutral-400">
        {{ eventsCount }} eventos
      </div>
    }
  </div>

  @if (loading) {
    <div class="flex items-center justify-center gap-3 py-12 text-sm text-neutral-500 dark:text-neutral-400">
      <svg class="h-5 w-5 animate-spin" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
      Carregando linha do tempo...
    </div>
  } @else {
    <div class="mb-3 flex gap-1 border-b border-neutral-200 dark:border-neutral-700">
      <button
        type="button"
        class="px-3 py-1.5 text-sm font-medium transition-colors"
        [ngClass]="activeTab() === 'consolidated'
          ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
          : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'"
        (click)="selectTab('consolidated')">
        Consolidada
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-sm font-medium transition-colors"
        [ngClass]="activeTab() === 'kruzer'
          ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
          : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'"
        (click)="selectTab('kruzer')">
        KRZ
      </button>
    </div>

    @if (visibleEvents.length > 0) {
      <ol class="relative">
        @for (event of visibleEvents; track $index; let last = $last) {
          <li class="grid grid-cols-[88px_16px_1fr] items-start gap-x-3 py-2.5"
            [ngClass]="{
              'rounded-md bg-rose-50/40 dark:bg-rose-900/10': event.status === 'err',
              'rounded-md bg-amber-50/40 dark:bg-amber-900/10': event.status === 'warn',
              'rounded-md bg-emerald-50/40 dark:bg-emerald-900/10': event.status === 'done',
              'opacity-40 grayscale': event.status === 'disabled',
              'cursor-pointer transition-colors hover:bg-neutral-100/60 dark:hover:bg-neutral-700/30': hasPayload(event)
            }"
            [attr.role]="hasPayload(event) ? 'button' : null"
            [attr.tabindex]="hasPayload(event) ? 0 : null"
            [attr.aria-expanded]="hasPayload(event) ? isExpanded($index) : null"
            (click)="toggleEvent(event, $index)"
            (keydown.enter)="toggleEvent(event, $index)"
            (keydown.space)="toggleEvent(event, $index); $event.preventDefault()">
            <div class="pt-0.5 text-right">
              @if (shouldShowDate($index)) {
                <div class="text-[10px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  {{ dateOf(event) }}
                </div>
              }
              <div class="font-mono text-sm font-bold tabular-nums text-neutral-900 dark:text-neutral-100"
                [ngClass]="{
                  'text-neutral-400 dark:text-neutral-500': event.status === 'disabled' || !timeOf(event)
                }">
                {{ timeOf(event) || '—' }}
              </div>
            </div>

            <div class="relative flex h-full justify-center">
              @if (!last) {
                <span class="absolute left-1/2 top-3 -ml-px h-full w-px bg-neutral-200 dark:bg-neutral-700" aria-hidden="true"></span>
              }
              <span class="relative z-10 mt-1.5 h-3 w-3 rounded-full ring-4 ring-white dark:ring-neutral-800"
                [ngClass]="{
                  'bg-blue-500 !ring-blue-500/20': event.status === 'default',
                  'bg-emerald-500 !ring-emerald-500/20': event.status === 'done',
                  'bg-amber-500 !ring-amber-500/20': event.status === 'warn',
                  'bg-rose-500 !ring-rose-500/20': event.status === 'err',
                  'bg-neutral-300 dark:bg-neutral-600': event.status === 'disabled'
                }"
                aria-hidden="true"></span>
            </div>

            <div class="min-w-0 flex flex-col gap-1">
              <div class="flex items-start justify-between gap-2">
                <div class="text-sm font-medium leading-snug"
                  [ngClass]="{
                    'text-rose-600 dark:text-rose-400 font-semibold': event.status === 'err',
                    'text-amber-600 dark:text-amber-400 font-semibold': event.status === 'warn',
                    'text-emerald-700 dark:text-emerald-400 font-semibold': event.status === 'done',
                    'text-neutral-500 dark:text-neutral-400': event.status === 'disabled',
                    'text-neutral-900 dark:text-neutral-100': event.status === 'default'
                  }">
                  {{ event.description }}
                </div>
                @if (hasPayload(event)) {
                  <span class="shrink-0 text-xs text-neutral-400 transition-transform"
                    [ngClass]="{ 'rotate-180': isExpanded($index) }"
                    aria-hidden="true">▾</span>
                }
              </div>
              <div class="inline-flex flex-wrap items-center gap-1.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                <span class="inline-flex items-center gap-1 rounded bg-neutral-100 px-1.5 py-0.5 font-mono tracking-normal text-neutral-700 dark:bg-neutral-700/60 dark:text-neutral-200">
                  @if (event.isHook) {
                    <span class="font-bold text-blue-500 dark:text-blue-400">hook</span>
                  }
                  {{ event.from }}
                </span>
                <span class="text-neutral-400">→</span>
                <span class="rounded bg-neutral-100 px-1.5 py-0.5 font-mono tracking-normal text-neutral-700 dark:bg-neutral-700/60 dark:text-neutral-200">{{ event.to }}</span>
              </div>

              @if (hasPayload(event) && isExpanded($index)) {
                <div class="relative mt-1" (click)="$event.stopPropagation()">
                  <button
                    type="button"
                    class="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded border border-neutral-300 bg-white px-2 py-1 text-xs font-medium text-neutral-700 shadow-sm transition-colors hover:bg-neutral-100 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                    [ngClass]="{ 'border-emerald-400 text-emerald-700 dark:border-emerald-500 dark:text-emerald-300': isCopied($index) }"
                    (click)="copyPayload(event, $index); $event.stopPropagation()">
                    @if (isCopied($index)) {
                      <i [innerHTML]="'check' | feather: { width: 12, height: 12 }"></i>
                      <span>Copiado</span>
                    } @else {
                      <i [innerHTML]="'copy' | feather: { width: 12, height: 12 }"></i>
                      <span>Copiar</span>
                    }
                  </button>
                  @if (isDfdlPayload(event.payload)) {
                    <div class="max-h-96 cursor-text overflow-y-auto rounded border border-neutral-200 bg-neutral-50 px-3 py-2 pr-24 font-mono text-xs dark:border-neutral-700 dark:bg-neutral-900">
                      @for (line of dfdlLines(event.payload); track $index; let even = $even) {
                        <div class="whitespace-pre-wrap break-words"
                          [ngClass]="even
                            ? 'text-neutral-900 dark:text-neutral-100'
                            : 'text-sky-700 dark:text-sky-300'">{{ line }}</div>
                      }
                    </div>
                  } @else if (isJsonPayload(event.payload)) {
                    <pre class="max-h-96 cursor-text overflow-y-auto whitespace-pre-wrap break-words rounded border border-neutral-200 bg-neutral-50 p-3 pr-24 font-mono text-xs text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
                      [innerHTML]="formatPayloadHtml(event.payload)"></pre>
                  } @else {
                    <pre class="max-h-96 cursor-text overflow-y-auto whitespace-pre-wrap break-words rounded border border-neutral-200 bg-neutral-50 p-3 pr-24 font-mono text-xs text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100">{{ formatPayload(event.payload) }}</pre>
                  }
                </div>
              }
            </div>
          </li>
        }
      </ol>
    } @else {
      <div class="py-3 text-center text-sm text-neutral-500 dark:text-neutral-400">Sem eventos registrados.</div>
    }
  }
</section>
